import { forwardRef, useImperativeHandle, useCallback, useState } from "react";
import { X, FileCode, Package, Eye, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Hint } from "@/components/ui/hint";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { usePropertyPanelHandlers } from "./hooks/usePropertyPanelHandlers";
import { usePropertyPanelState } from "./hooks/usePropertyPanelState";
import { CSSPropertiesSection } from "./sections/CSSPropertiesSection";
import { TypographySection } from "./sections/TypographySection";
import { ShadowsSection } from "./sections/ShadowsSection";
import { LayoutSection } from "./sections/LayoutSection";
import { BorderSection } from "./sections/BorderSection";
import { SpacingSection } from "./sections/SpacingSection";
import { VideoSettingsSection } from "./sections/VideoSettingsSection";
import { AudioSettingsSection } from "./sections/AudioSettingsSection";
import { ColorPicker } from "../ui/color-picker";
import { TailwindColorSelectorPopover } from "../editor/themes/components/tailwind-color-selector-popover";
import { TailwindClassInput } from "./components/tailwind-class-input";
import PropertySection from "./PropertySection";
import {
  PropertyPanelProps,
  PropertyPanelRef,
} from "@/types/property-panel.types";
import {
  OBJECT_FIT_OPTIONS,
  VISIBILITY_OPTIONS,
} from "@/constants/property-panel.constants";

/** Map tag names to readable labels, with icon detection */
function getElementLabel(
  tagName?: string,
  attributes?: Record<string, unknown>,
): string {
  // Detect icon component via data-icon-name attribute
  if (attributes?.["data-icon-name"]) return "Icon";
  // Detect avatar (round img)
  if (attributes?.["data-component-type"] === "avatar") return "Avatar";

  const labels: Record<string, string> = {
    h1: "Heading 1",
    h2: "Heading 2",
    h3: "Heading 3",
    h4: "Heading 4",
    h5: "Heading 5",
    h6: "Heading 6",
    p: "Paragraph",
    a: "Link",
    button: "Button",
    img: "Image",
    div: "Container",
    span: "Inline",
    section: "Section",
    nav: "Navigation",
    header: "Header",
    footer: "Footer",
    main: "Main",
    ul: "List",
    ol: "Ordered List",
    li: "List Item",
    input: "Input",
    textarea: "Textarea",
    form: "Form",
    video: "Video",
    audio: "Audio",
    svg: "SVG",
  };
  return labels[tagName || ""] || "Element";
}

const PropertyPanel = forwardRef<PropertyPanelRef, PropertyPanelProps>(
  ({ element, onChange, onDiscard, readOnly = false, onAiCommand }, ref) => {
    const [activeTab, setActiveTab] = useState<string>("style");

    // State management
    const state = usePropertyPanelState(element);
    const {
      content,
      setContent,
      styles,
      setStyles,
      cssProperties,
      setCssProperties,
      visibility,
      setVisibility,
      textShadow,
      setTextShadow,
      boxShadow,
      setBoxShadow,
      linkUrlButton,
      setLinkUrlButton,
      imageSrc,
      setImageSrc,
      classNames,
      setClassNames,
      localOpacity,
      setLocalOpacity,
      videoSettings,
      setVideoSettings,
      iconName,
      setIconName,
      elementId,
      setElementId,
    } = state;

    // Event handlers
    const handlers = usePropertyPanelHandlers({
      onChange,
      element,
      styles,
      setStyles,
      textShadow,
      setTextShadow,
      boxShadow,
      setBoxShadow,
      setContent,
      setLinkUrlButton,
      setImageSrc,
      setClassNames,
      setVisibility,
      setLocalOpacity,
      cssProperties,
      setCssProperties,
      setVideoSettings,
      setIconName,
      setElementId,
    });

    const {
      handleStyleChange,
      handleSpacingChange,
      handleBatchSpacingChange,
      handleBatchStyleChange,
      handleShadowChange,
      handleVisibilityChange,
      handleContentChange,
      handleLinkUrlChange,
      handleSrcChange,
      handleClassNameChange,
      handleLetterSpacingChange,
      handleLineHeightChange,
      handleOpacityChange,
      handleOpacityCommit,
      addNewCssProperty,
      removeCssProperty,
      updateCssProperty,
      clearAllCssProperties,
      handleVideoSettingChange,
      handleIconNameChange,
      handleElementIdChange,
    } = handlers;

    // Handle Ask OptiDev to edit button click
    const handleAskAiToEdit = useCallback(() => {
      if (!element || !onAiCommand) return;

      const originalContent = element.content || "";
      const tagName = element.tagName || "element";
      const prompt = `Change ${tagName} text from "${originalContent}" to "${content}"`;

      onAiCommand(prompt);
    }, [element, content, onAiCommand]);

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      updateContent: (newContent: string) => {
        setContent(newContent);
      },
      updateStyles: (styleUpdates: Record<string, string>) => {
        setStyles((prev) => ({
          ...prev,
          ...styleUpdates,
        }));
      },
    }));

    // Show empty state when no element is selected
    if (!element) {
      return (
        <div className="flex flex-col h-full bg-background overflow-hidden">
          <div className="h-12 px-4 border-b border-border flex items-center bg-background">
            <span className="text-sm font-medium text-foreground">
              Visual Editor
            </span>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            <div className="rounded-full bg-primary/10 p-3 mb-3">
              <Package className="w-8 h-8 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground max-w-xs">
              Click &quot;Select Element&quot; to start editing your design
              visually
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full bg-background overflow-hidden">
        {/* Header */}
        <div className="h-12 px-4 border-b border-border flex items-center justify-between bg-background">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground">
              {element?.tagName || "Element"}
            </span>
            <span className="text-xs text-muted-foreground">
              {getElementLabel(element?.tagName, element?.attributes)}
            </span>
          </div>
          {onDiscard && (
            <Hint text="Close panel" delayDuration={0}>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 cursor-pointer text-muted-foreground hover:text-foreground"
                onClick={onDiscard}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </Hint>
          )}
        </div>

        {/* Scrollable content */}
        <ScrollArea className="flex-1">
          <div className="flex flex-col gap-4 p-4">
            {/* Read-only banner */}
            {readOnly && (
              <div className="flex items-center gap-2 p-2 bg-muted rounded-lg border border-border">
                <Eye className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <span className="text-xs text-muted-foreground">
                  View only - editing is disabled
                </span>
              </div>
            )}

            {/* Source Location */}
            {element?.source && (
              <div className="p-2 bg-muted rounded-lg border border-border">
                <div className="flex items-center gap-2">
                  <FileCode className="w-3 h-3 text-primary" />
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">
                    Source
                  </span>
                </div>
                <p className="text-xs text-foreground mt-1 font-mono truncate">
                  {element.source.fileName}:{element.source.lineNumber}:
                  {element.source.columnNumber}
                </p>
              </div>
            )}

            {/* Element ID (for anchor links) */}
            <div className="flex flex-col gap-2">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Element ID
              </h3>
              <Input
                className="w-full h-8 bg-background border-border text-foreground text-xs font-mono"
                value={elementId}
                onChange={(e) => handleElementIdChange(e.target.value)}
                disabled={readOnly}
                placeholder="e.g. hero, features, pricing"
              />
              {elementId && (
                <p className="text-[10px] text-muted-foreground">
                  Link to this section with{" "}
                  <span className="font-mono text-foreground">
                    href=&quot;#{elementId}&quot;
                  </span>
                </p>
              )}
            </div>

            {/* Content */}
            {element?.content && (
              <div className="flex flex-col gap-2">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Content
                </h3>
                <textarea
                  value={content}
                  onChange={(e) => handleContentChange(e.target.value)}
                  disabled={readOnly}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground focus:border-primary focus:outline-none resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                  rows={4}
                  placeholder="Enter content..."
                />

                {/* Ask OptiDev to edit button */}
                {element?.bind && onAiCommand && !readOnly && (
                  <Button
                    size="sm"
                    className="w-full h-8 text-xs gap-1.5"
                    onClick={handleAskAiToEdit}
                    disabled={content === element?.content}
                  >
                    <Sparkles className="w-3 h-3" />
                    Ask OptiDev to edit
                  </Button>
                )}
              </div>
            )}

            {/* Button Properties */}
            {element.tagName === "a" && (
              <div className="flex flex-col gap-2">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Link URL
                </h3>
                <Input
                  className="w-full h-8 bg-background border-border text-foreground text-xs"
                  value={linkUrlButton}
                  onChange={(e) => handleLinkUrlChange(e.target.value)}
                  disabled={readOnly}
                  placeholder="Enter URL..."
                />
                <p className="text-[10px] text-muted-foreground">
                  Use <span className="font-mono text-foreground">#sectionId</span> to scroll to a section, or <span className="font-mono text-foreground">/page</span> to navigate to another page
                </p>
              </div>
            )}

            {/* Media Source Properties (Image, Video, Audio) */}
            {(element.tagName === "img" ||
              element.tagName === "video" ||
              element.tagName === "audio") && (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-2">
                  <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {element.tagName === "img"
                      ? "Image"
                      : element.tagName === "video"
                        ? "Video"
                        : "Audio"}{" "}
                    Source
                  </h3>
                  <Input
                    className="w-full h-8 bg-background border-border text-foreground text-xs"
                    value={imageSrc}
                    onChange={(e) => handleSrcChange(e.target.value)}
                    disabled={readOnly}
                    placeholder={`Enter ${element.tagName === "audio" ? "audio" : element.tagName === "video" ? "video" : "image"} URL...`}
                  />
                </div>
                {/* Object Fit - only for img and video */}
                {(element.tagName === "img" || element.tagName === "video") && (
                  <div className="flex flex-col gap-2">
                    <Label className="text-xs font-medium text-muted-foreground">
                      Object Fit
                    </Label>
                    <Select
                      value={styles.objectFit || "fill"}
                      onValueChange={(value) =>
                        handleStyleChange("objectFit", value)
                      }
                      disabled={readOnly}
                    >
                      <SelectTrigger className="w-full h-8 bg-background border-border text-foreground text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {OBJECT_FIT_OPTIONS.map((fit: string) => (
                          <SelectItem key={fit} value={fit}>
                            {fit.charAt(0).toUpperCase() +
                              fit.slice(1).replace("-", " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {/* Video Settings */}
            {element.tagName === "video" && (
              <VideoSettingsSection
                videoSettings={videoSettings}
                onSettingChange={handleVideoSettingChange}
                disabled={readOnly}
              />
            )}

            {/* Audio Settings */}
            {element.tagName === "audio" && (
              <AudioSettingsSection
                audioSettings={videoSettings}
                onSettingChange={handleVideoSettingChange}
                elementAttributes={element.attributes}
                onAttributeChange={(attr, value) => {
                  onChange?.("attributes", { [attr]: value });
                }}
                disabled={readOnly}
              />
            )}

            {/* Icon Properties */}
            {element.attributes?.["data-icon-name"] && (
              <div className="flex flex-col gap-2">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Icon
                </h3>
                <Input
                  className="w-full h-8 bg-background border-border text-foreground text-xs"
                  value={iconName}
                  onChange={(e) => handleIconNameChange(e.target.value)}
                  disabled={readOnly}
                  placeholder="e.g. Heart, Star, ArrowRight"
                />
                <p className="text-[10px] text-muted-foreground">
                  Lucide icon name
                </p>
              </div>
            )}

            {/* Tabs: Style | Layout | Advanced */}
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <TabsList className="mb-3 grid w-full grid-cols-3 bg-muted p-1 h-9">
                <TabsTrigger
                  value="style"
                  className="cursor-pointer text-xs text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                >
                  Style
                </TabsTrigger>
                <TabsTrigger
                  value="layout"
                  className="cursor-pointer text-xs text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                >
                  Layout
                </TabsTrigger>
                <TabsTrigger
                  value="advanced"
                  className="cursor-pointer text-xs text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                >
                  Advanced
                </TabsTrigger>
              </TabsList>

              {/* Style Tab */}
              <TabsContent value="style">
                <div className="space-y-4">
                  {/* Typography */}
                  <TypographySection
                    styles={styles}
                    onStyleChange={handleStyleChange}
                    onLetterSpacingChange={handleLetterSpacingChange}
                    onLineHeightChange={handleLineHeightChange}
                  />

                  {/* Background */}
                  <PropertySection title="Background">
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1">
                        Fill Color
                      </Label>
                      <div className="flex items-center gap-2">
                        <ColorPicker
                          value={styles.backgroundColor}
                          onChange={(value) =>
                            handleStyleChange("backgroundColor", value)
                          }
                          className="flex-shrink-0 h-8 w-8 border-border"
                          disabled={readOnly}
                        />
                        <TailwindColorSelectorPopover
                          value={styles.backgroundColor}
                          onSelect={(color) =>
                            handleStyleChange("backgroundColor", color)
                          }
                        />
                        <Input
                          value={styles.backgroundColor}
                          onChange={(e) =>
                            handleStyleChange("backgroundColor", e.target.value)
                          }
                          disabled={readOnly}
                          className="h-8 bg-background border-border text-foreground text-xs font-mono"
                          placeholder="#FFFFFF"
                        />
                      </div>
                      <Button
                        variant={
                          styles.backgroundColor === "transparent"
                            ? "secondary"
                            : "outline"
                        }
                        size="sm"
                        className="w-full h-7 text-xs mt-2"
                        onClick={() =>
                          handleStyleChange("backgroundColor", "transparent")
                        }
                        disabled={readOnly}
                      >
                        <span
                          className="inline-block w-3 h-3 mr-1.5 rounded-sm border border-border"
                          style={{
                            backgroundImage:
                              "linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)",
                            backgroundSize: "6px 6px",
                            backgroundPosition:
                              "0 0, 0 3px, 3px -3px, -3px 0px",
                          }}
                        />
                        Transparent
                      </Button>
                    </div>
                  </PropertySection>

                  {/* Border */}
                  <BorderSection
                    styles={styles}
                    onStyleChange={handleStyleChange}
                    onBatchStyleChange={handleBatchStyleChange}
                  />

                  {/* Shadows & Effects */}
                  <ShadowsSection
                    localOpacity={localOpacity}
                    textShadow={textShadow}
                    boxShadow={boxShadow}
                    onOpacityChange={handleOpacityChange}
                    onOpacityCommit={handleOpacityCommit}
                    onShadowChange={handleShadowChange}
                  />
                </div>
              </TabsContent>

              {/* Layout Tab */}
              <TabsContent value="layout">
                <div className="space-y-4">
                  {/* Dimensions */}
                  <LayoutSection
                    styles={styles}
                    onStyleChange={handleStyleChange}
                  />

                  {/* Spacing (Margin & Padding) */}
                  <SpacingSection
                    styles={styles}
                    onStyleChange={handleStyleChange}
                    onSpacingChange={handleSpacingChange}
                    onBatchSpacingChange={handleBatchSpacingChange}
                  />

                  {/* Visibility */}
                  <PropertySection title="Visibility">
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-col gap-1.5">
                        <Label className="text-xs text-muted-foreground">
                          Responsive Visibility
                        </Label>
                        <Select
                          value={visibility}
                          onValueChange={(value: string) =>
                            handleVisibilityChange(
                              value as "none" | "desktop" | "tablet" | "mobile",
                            )
                          }
                          disabled={readOnly}
                        >
                          <SelectTrigger className="w-full bg-background border border-border rounded-md text-foreground h-8 text-xs">
                            <SelectValue placeholder="Select visibility" />
                          </SelectTrigger>
                          <SelectContent>
                            {VISIBILITY_OPTIONS.map(
                              ({
                                value,
                                label,
                              }: {
                                value: string;
                                label: string;
                              }) => (
                                <SelectItem
                                  key={value}
                                  value={value}
                                  className="text-xs"
                                >
                                  {label}
                                </SelectItem>
                              ),
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      {visibility !== "none" && (
                        <p className="text-[10px] text-muted-foreground/70">
                          Effect applies after save (uses Tailwind responsive
                          classes)
                        </p>
                      )}
                    </div>
                  </PropertySection>
                </div>
              </TabsContent>

              {/* Advanced Tab */}
              <TabsContent value="advanced">
                <div className="space-y-4">
                  {/* ClassNames */}
                  <PropertySection title="Classes" defaultOpen>
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1">
                        Tailwind CSS Classes
                      </Label>
                      <TailwindClassInput
                        value={classNames}
                        onChange={handleClassNameChange}
                        disabled={readOnly}
                      />
                    </div>
                  </PropertySection>

                  {/* CSS Properties */}
                  <CSSPropertiesSection
                    cssProperties={cssProperties}
                    onAddNew={addNewCssProperty}
                    onRemove={removeCssProperty}
                    onUpdate={updateCssProperty}
                    onClearAll={clearAllCssProperties}
                  />
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </ScrollArea>
      </div>
    );
  },
);

PropertyPanel.displayName = "PropertyPanel";

export default PropertyPanel;
